/**
 * SPECTATOR ON START
 *
 * Quality-of-life for CTF: when a new match starts, if you give no input for
 * a few seconds, automatically enter spectator mode. Captures the "I was AFK
 * or watching, didn't intend to play this round" intent without requiring an
 * explicit /spectate.
 *
 * UI.ts arms this off the "Game starting!" info message, sent the moment the
 * new match begins. Input.ts reports every press from every path (keyboard,
 * touch, gamepad, mouse mode) while the grace window is open, and when the
 * timer fires it also asks whether anything is still held, so a course held
 * through the window counts as playing too. Any of that cancels; the timer
 * firing unopposed calls spectateForce.
 *
 * Modeled loosely on SWAM's SpectatorOnStart (engine-swam-only.js, see the
 * [HARVESTED] marker) but uses idle-detection rather than a "were you formally
 * in spec" check: it matches actual user intent ("I'm not pressing anything,
 * don't put me in the match") regardless of whether spec mode was explicit.
 */
import { GAME_TYPES } from '@airmash/protocol';
import { spectateForce } from './Network';
import { session } from './Session';

/** Grace window after match start to detect input. */
const IDLE_GRACE_MS = 3000;

let timerId: number | null = null;

/** Called from Input.ts on every press from any input path: the player has opted into playing. */
export function spectatorOnStartHandleMovementIntent(): void {
  cancelSpectatorOnStart();
}

/**
 * Arms the grace window at a CTF match start. `isInputHeld` is asked when the
 * window closes, so input held throughout, which produces no fresh presses,
 * still counts as playing.
 */
export function armSpectatorOnStart(isInputHeld: () => boolean): void {
  if (session.gameType !== GAME_TYPES.CTF) return;

  cancelSpectatorOnStart();

  timerId = window.setTimeout(() => {
    timerId = null;
    // Re-check at fire time: gameType could have changed in the window (game
    // switch, disconnect) and we don't want a stray spectate outside CTF.
    if (session.gameType !== GAME_TYPES.CTF) return;
    if (isInputHeld()) return;
    spectateForce();
  }, IDLE_GRACE_MS);
}

/** Aborts any pending auto-spectate; also called from reconnection cleanup. */
export function cancelSpectatorOnStart(): void {
  if (timerId !== null) {
    clearTimeout(timerId);
    timerId = null;
  }
}
